import Color from 'color';

import addCircleIcon from '@/public/icons/add-circle-icon.svg';
import deleteIcon from '@/public/icons/delete-icon.svg';
import editIcon from '@/public/icons/edit-icon.svg';
import extendIcon from '@/public/icons/extend-icon.svg';
import eyeIcon from '@/public/icons/eye-icon.svg';
import minimizeIcon from '@/public/icons/minimize-icon.svg';
import qrcodeIcon from '@/public/icons/qr-code.svg';
import searchIcon from '@/public/icons/search-icon.svg';

const colorPalette = {
    mainBackground: '#3F4A63',
    secondBackground: '#4b5671',
    thirdBackground: '#57627E',
    frontColor: '#fff',
    darkColor: '#323B50',
    clearColor: '#7D87A0',
    activeColorLighter: '#C3EAFF',
    activeColor: '#6CC2F5',
    activeColorDarker: '#56A6D6',

    create: '#48A6AB',
    createDarker: '#21717A',
    delete: '#E86868',
    deleteDarker: '#9A1A1A',
    edit: '#6CC2F5',
    editDarker: '#F36F4E',

    danger: '#d55e5e',
    dangerDark: '#C34B4B',
    dangerClear: '#FF6775',
    dangerFrontClear: '#f5e8e8',
    silver: '#999',
    warning: '#ff917a',
    neutral: '#777',
    dark: '#132231',

    orange: '#EA6939',
    success: '#48A6AB',
    info: '#3d9cd3',

    signalTypeDark: {
        pulseRate: '#65D376',
        spo2: '#5799F2',
        respiratoryRate: '#b276f8',
        movements: '#E9D856',
        unknown: '#BDBDBD',
    },
    
    signalType: {
        pulseRate: '#65D376',
        spo2: '#5799F2',
        respiratoryRate: '#b276f8',
        movements: '#E9D856',
        unknown: '#BDBDBD',
    },

    signalTypeLight: {
        pulseRate: '#a5ecb0',
        spo2: '#A9CCF9',
        respiratoryRate: '#D9C4F8',
        movements: '#E9D856',
        unknown: '#a5ecb0',
    },

    signalTypeClear: {
        pulseRate: '#dafadc',
        spo2: '#deedff',
        respiratoryRate: '#ede1ff',
        movements: '#fcf5b7',
        unknown: '#dafadc',
    },
};

const colors = {
    colorPalette: colorPalette,

    body: {
        background: colorPalette.mainBackground,
        text: colorPalette.frontColor
    },

    overlay: {
        background: colorPalette.secondBackground,
    },

    defaultText: {
        text: colorPalette.frontColor,
    },

    menu: {
        background: colorPalette.thirdBackground,
        backgroundHover: colorPalette.thirdBackground,
        backgroundActive: colorPalette.thirdBackground,
        text: colorPalette.frontColor,
        textHover: colorPalette.activeColor,
        picto: colorPalette.frontColor,
    },

    submenu: {
        background: '#241534', //colorPalette.dark,
        backgroundHover: rgba(colorPalette.frontColor, 0.15),
        //backgroundActive: `linear-gradient(to right, ${lighten(colorPalette.dark, 10)} 0%, ${lighten(colorPalette.dark, -15)} 100%)`,
        backgroundActive: rgba(colorPalette.frontColor, 0.2),
        text: colorPalette.darkColor,
        textHover: colorPalette.darkColor,
        textActive: colorPalette.orange,
    },
    
    checkbox: {
        background: colorPalette.activeColor,
        backgroundHover: colorPalette.activeColorDarker,
        backgroundDisabled: '#868686',
    },

    button: {
        background: colorPalette.thirdBackground,
        backgroundHover: lighten(colorPalette.thirdBackground, -5),
        backgroundDisabled: '#868686',
        text: colorPalette.frontColor,
        loadingColor: colorPalette.activeColor
    },

    buttonCreate: {
        background: colorPalette.create,
        backgroundHover: lighten(colorPalette.create, -7),
        backgroundIcon: `url(${addCircleIcon})`,
        text: colorPalette.frontColor,
    },

    buttonSearch: {
        background: colorPalette.activeColor,
        backgroundHover: lighten(colorPalette.activeColor, -7),
        backgroundIcon: `url(${searchIcon})`,
        text: colorPalette.frontColor,
    },

    buttonActive: {
        background: colorPalette.activeColor,
        backgroundHover: lighten(colorPalette.activeColor, -7),
        backgroundIcon: `url(${addCircleIcon})`,
        text: colorPalette.frontColor,
    },

    buttonEdit: {
        defaultBackground: colorPalette.thirdBackground,
        background: colorPalette.edit,
        backgroundHover: lighten(colorPalette.edit, -15),
        backgroundIcon: `url(${editIcon})`,
        text: colorPalette.frontColor,
    },

    buttonExtend: {
        defaultBackground: colorPalette.thirdBackground,
        background: colorPalette.activeColor,
        backgroundHover: lighten(colorPalette.activeColor, -15),
        backgroundIcon: `url(${extendIcon})`,
        text: colorPalette.frontColor,
    },

    buttonMinimize: {
        defaultBackground: colorPalette.thirdBackground,
        background: colorPalette.activeColor,
        backgroundHover: lighten(colorPalette.activeColor, -15),
        backgroundIcon: `url(${minimizeIcon})`,
        text: colorPalette.frontColor,
    },

    buttonDelete: {
        defaultBackground: colorPalette.thirdBackground,
        background: colorPalette.delete,
        backgroundHover: lighten(colorPalette.delete, -15),
        backgroundIcon: `url(${deleteIcon})`,
        text: colorPalette.frontColor,
    },

    buttonHide: {
        defaultBackground: colorPalette.thirdBackground,
        background: colorPalette.delete,
        backgroundHover: lighten(colorPalette.delete, -15),
        backgroundIcon: `url(${eyeIcon})`,
        text: colorPalette.frontColor,
    },

    buttonCancel: {
        background: colorPalette.clearColor,
        backgroundHover: lighten(colorPalette.clearColor, -15),
        text: colorPalette.frontColor,
    },

    buttonOutline: {
        background: colorPalette.frontColor,
        backgroundHover: lighten(colorPalette.activeColor, 0),
        backgroundFocus: lighten(colorPalette.activeColor, -10),
        backgroundActive: lighten(colorPalette.activeColor, -17),
        text: colorPalette.activeColor,
        textHover: colorPalette.frontColor,
        textFocus: colorPalette.frontColor,
        border: lighten(colorPalette.activeColor, -2),
    },

    buttonQrCode: {
        defaultBackground: colorPalette.thirdBackground,
        background: colorPalette.edit,
        backgroundHover: lighten(colorPalette.edit, -15),
        backgroundIcon: `url(${qrcodeIcon})`,
        text: colorPalette.frontColor,
    },

    link: {
        text: colorPalette.activeColor,
    },

    table: {
        border: colorPalette.mainBackground,
    },

    tableHeader: {
        background: 'transparent',
        text: colorPalette.frontColor,
    },

    tableRow: {
        background: colorPalette.secondBackground,
        backgroundHover: desaturate(lighten(colorPalette.secondBackground, 55), 60),
    },

    tableRowSub: {
        background: lighten(colorPalette.secondBackground, -3),
        backgroundHover: lighten(colorPalette.secondBackground, -3),//desaturate(lighten(colorPalette.main, 47), 70),
    },

    lowFocusText: {
        text: colorPalette.silver, // FIXME deprecated; use colors.lowFocus.text
    },

    lowFocus: {
        text: colorPalette.darkColor,
    },
};

const sizes = {
    menu: {
        widthMd: '200px',
        widthLg: '220px',
        widthXl: '240px',
    },
    header: {
        height: '100px',
    },
};

const zIndices = {
    header: 940,
    sidebar: 940,
    overlays: 1050, //hardcoded in the lib
    dateInput: 1051,
    feedbackAlerts: 1052,
    debug: 2000,
};

const bootstrapMediaSizes = {
    xsMin: 0,
    smMin: 576,
    mdMin: 768,
    lgMin: 992,
    xlMin: 1200,

    xsMax: 575,
    smMax: 767,
    mdMax: 991,
    lgMax: 1199,
    xlMax: 99999,
};

const medias = {
    xs: `min-width: ${bootstrapMediaSizes.xsMin}px) and (max-width: ${bootstrapMediaSizes.xsMax}px`,
    sm: `min-width: ${bootstrapMediaSizes.smMin}px) and (max-width: ${bootstrapMediaSizes.smMax}px`,
    md: `min-width: ${bootstrapMediaSizes.mdMin}px) and (max-width: ${bootstrapMediaSizes.mdMax}px`,
    lg: `min-width: ${bootstrapMediaSizes.lgMin}px) and (max-width: ${bootstrapMediaSizes.lgMax}px`,
    xl: `min-width: ${bootstrapMediaSizes.xlMin}px)`,

    xsMin: `min-width: ${bootstrapMediaSizes.xsMin}px`,
    smMin: `min-width: ${bootstrapMediaSizes.smMin}px`,
    mdMin: `min-width: ${bootstrapMediaSizes.mdMin}px`,
    lgMin: `min-width: ${bootstrapMediaSizes.lgMin}px`,
    xlMin: `min-width: ${bootstrapMediaSizes.xlMin}px`,

    xsMax: `max-width: ${bootstrapMediaSizes.xsMax}px`,
    smMax: `max-width: ${bootstrapMediaSizes.smMax}px`,
    mdMax: `max-width: ${bootstrapMediaSizes.mdMax}px`,
    lgMax: `max-width: ${bootstrapMediaSizes.lgMax}px`,
    xlMax: `max-width: ${bootstrapMediaSizes.xlMax}px`,
};

const defaultUserPic = 'data:image/svg+xml;base64,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';

export {
    colorPalette,
    colors,
    sizes,
    zIndices,
    medias,
    bootstrapMediaSizes,
    defaultUserPic,
};

export function lighten(color, amount) {
    const lab = Color(color).lab();
    lab.color[0] -= amount;
    const out = lab.rgb();
    return rgbToHex(...out.color);
}

export function desaturate(color, amount) {
    let lab = Color(color);
    lab = lab.desaturate(amount/100.0);
    const out = lab.rgb();
    return rgbToHex(...out.color);
}

function componentToHex(c) {
    const hex = Math.round(c).toString(16);
    return hex.length === 1 ? `0${hex}` : hex;
}

function rgbToHex(r, g, b) {
    return `#${componentToHex(r)}${componentToHex(g)}${componentToHex(b)}`;
}

export function rgba(color, ratio) {
    color = Color(color);
    const out = color.rgb().color;
    return `rgba(${out[0]},${out[1]},${out[2]}, ${ratio})`;
}
